import { Controller } from "@hotwired/stimulus"

/**
 * Section-level form management for CV Builder.
 * Handles section collapse/expand, add/remove entries.
 * One instance per section (experience, education, skills, etc.).
 */
export default class extends Controller {
  static values = { section: String }
  static targets = ["sectionBody", "chevron", "entries"]

  stopPropagation(event) {
    event.stopPropagation()
  }

  toggleSection() {
    if (!this.hasSectionBodyTarget) return

    const body = this.sectionBodyTarget
    const hidden = body.classList.toggle("hidden")

    if (this.hasChevronTarget) {
      this.chevronTarget.classList.toggle("rotate-180", hidden)
    }
  }

  addEntry() {
    if (!this.hasEntriesTarget) return

    const section = this.sectionValue
    const index = this.entriesTarget.querySelectorAll("[data-entry-id]").length
    const id = `${section}_${crypto.randomUUID()}`
    const html = this._templateFor(section, index, id)

    if (html) {
      this.entriesTarget.insertAdjacentHTML("beforeend", html)
      this._reindex()
    }
  }

  removeEntry(event) {
    const entry = event.target.closest("[data-entry-id]")
    if (entry) {
      entry.remove()
      this._reindex()
      this._dispatchChange()
    }
  }

  _reindex() {
    const entries = this.entriesTarget.querySelectorAll("[data-entry-id]")
    const section = this.sectionValue

    entries.forEach((entry, idx) => {
      entry.querySelectorAll("[name]").forEach((input) => {
        input.name = input.name.replace(
          new RegExp(`${section}\\[\\d+\\]`),
          `${section}[${idx}]`
        )
      })
    })
  }

  _dispatchChange() {
    this.element.dispatchEvent(
      new Event("input", { bubbles: true })
    )
  }

  _templateFor(section, index, id) {
    const templates = {
      experience: `
        <div class="border border-gray-200 rounded-lg p-3 mb-3" data-entry-id="${id}">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-700">New Position</span>
            <button type="button" class="text-gray-400 hover:text-red-500 text-xs"
                    data-action="click->cv-builder-form#removeEntry" aria-label="Remove entry"><i class="fas fa-trash-alt"></i></button>
          </div>
          <input type="hidden" name="experience[${index}].id" value="${id}">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input type="text" name="experience[${index}].title" class="cv-input" placeholder="Job Title" data-action="input->cv-builder#onChange">
            <input type="text" name="experience[${index}].company" class="cv-input" placeholder="Company" data-action="input->cv-builder#onChange">
            <input type="text" name="experience[${index}].location" class="cv-input" placeholder="Location" data-action="input->cv-builder#onChange">
            <div class="flex gap-2">
              <input type="text" name="experience[${index}].start_date" class="cv-input flex-1" placeholder="2021-03" data-action="input->cv-builder#onChange">
              <input type="text" name="experience[${index}].end_date" class="cv-input flex-1" placeholder="2024-01" data-action="input->cv-builder#onChange">
            </div>
          </div>
          <label class="flex items-center gap-2 mt-2 text-xs text-gray-600">
            <input type="checkbox" name="experience[${index}].current" class="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500" data-action="change->cv-builder#onChange">
            Currently working here
          </label>
          <div class="mt-2">
            <label class="block text-xs font-medium text-gray-600 mb-1">Key achievements (one per line)</label>
            <textarea name="experience[${index}].bullets" rows="4" class="cv-input text-xs" placeholder="Led migration of payment processing..." data-action="input->cv-builder#onChange"></textarea>
          </div>
        </div>`,

      education: `
        <div class="border border-gray-200 rounded-lg p-3 mb-3" data-entry-id="${id}">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-700">New Qualification</span>
            <button type="button" class="text-gray-400 hover:text-red-500 text-xs"
                    data-action="click->cv-builder-form#removeEntry" aria-label="Remove entry"><i class="fas fa-trash-alt"></i></button>
          </div>
          <input type="hidden" name="education[${index}].id" value="${id}">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input type="text" name="education[${index}].institution" class="cv-input" placeholder="University of Auckland" data-action="input->cv-builder#onChange">
            <input type="text" name="education[${index}].degree" class="cv-input" placeholder="Bachelor of Science" data-action="input->cv-builder#onChange">
            <input type="text" name="education[${index}].field" class="cv-input" placeholder="Computer Science" data-action="input->cv-builder#onChange">
            <div class="flex gap-2">
              <input type="text" name="education[${index}].start_date" class="cv-input flex-1" placeholder="2012-03" data-action="input->cv-builder#onChange">
              <input type="text" name="education[${index}].end_date" class="cv-input flex-1" placeholder="2015-11" data-action="input->cv-builder#onChange">
            </div>
          </div>
          <div class="mt-2">
            <textarea name="education[${index}].description" rows="2" class="cv-input text-xs" placeholder="Relevant courses, honours, or notes (optional)" data-action="input->cv-builder#onChange"></textarea>
          </div>
        </div>`,

      skills: `
        <div class="border border-gray-200 rounded-lg p-3 mb-3" data-entry-id="${id}">
          <input type="hidden" name="skills[${index}].id" value="${id}">
          <div class="flex items-center justify-between mb-2">
            <input type="text" name="skills[${index}].category" class="cv-input text-sm font-medium" placeholder="Category name (e.g. Languages & Frameworks)" data-action="input->cv-builder#onChange">
            <button type="button" class="text-gray-400 hover:text-red-500 text-xs ml-2"
                    data-action="click->cv-builder-form#removeEntry" aria-label="Remove entry"><i class="fas fa-trash-alt"></i></button>
          </div>
          <input type="text" name="skills[${index}].items" class="cv-input text-xs" placeholder="Ruby, Rails, TypeScript, React (comma-separated)" data-action="input->cv-builder#onChange">
        </div>`,

      certifications: `
        <div class="border border-gray-200 rounded-lg p-3 mb-3" data-entry-id="${id}">
          <input type="hidden" name="certifications[${index}].id" value="${id}">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-700">New Certification</span>
            <button type="button" class="text-gray-400 hover:text-red-500 text-xs"
                    data-action="click->cv-builder-form#removeEntry" aria-label="Remove entry"><i class="fas fa-trash-alt"></i></button>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input type="text" name="certifications[${index}].name" class="cv-input sm:col-span-2" placeholder="AWS Solutions Architect" data-action="input->cv-builder#onChange">
            <input type="text" name="certifications[${index}].date" class="cv-input" placeholder="2023-06" data-action="input->cv-builder#onChange">
          </div>
          <input type="text" name="certifications[${index}].issuer" class="cv-input mt-2" placeholder="Issuing organisation" data-action="input->cv-builder#onChange">
        </div>`,

      languages: `
        <div class="flex items-center gap-2 mb-2" data-entry-id="${id}">
          <input type="hidden" name="languages[${index}].id" value="${id}">
          <input type="text" name="languages[${index}].name" class="cv-input flex-1" placeholder="English" data-action="input->cv-builder#onChange">
          <select name="languages[${index}].proficiency" class="cv-input w-36" data-action="change->cv-builder#onChange">
            <option value="native">Native</option>
            <option value="fluent">Fluent</option>
            <option value="advanced">Advanced</option>
            <option value="intermediate">Intermediate</option>
            <option value="basic">Basic</option>
          </select>
          <button type="button" class="text-gray-400 hover:text-red-500 text-xs"
                  data-action="click->cv-builder-form#removeEntry"><i class="fas fa-trash-alt"></i></button>
        </div>`,

      achievements: `
        <div class="border border-gray-200 rounded-lg p-3 mb-3" data-entry-id="${id}">
          <input type="hidden" name="achievements[${index}].id" value="${id}">
          <div class="flex items-center justify-between mb-2">
            <input type="text" name="achievements[${index}].title" class="cv-input text-sm font-medium flex-1" placeholder="Speaker — RubyConf AU 2023" data-action="input->cv-builder#onChange">
            <button type="button" class="text-gray-400 hover:text-red-500 text-xs ml-2"
                    data-action="click->cv-builder-form#removeEntry" aria-label="Remove entry"><i class="fas fa-trash-alt"></i></button>
          </div>
          <textarea name="achievements[${index}].description" rows="2" class="cv-input text-xs" placeholder="Brief description (optional)" data-action="input->cv-builder#onChange"></textarea>
        </div>`,
    }

    return templates[section] || null
  }
}
